/**
 * Web Auth Bridge Client Helper
 *
 * WHAT THIS FILE DOES:
 * - Provides a tiny helper for syncing auth state between gist.is web pages and the extension.
 * - Normalizes the DOM event contract used by the extension content script.
 * - Dispatches auth import/logout events to the extension via CustomEvent.
 * - Listens for gist-auth-state events and forwards them to a callback.
 *
 * WHY THIS EXISTS:
 * - Web pages should not duplicate event wiring boilerplate.
 * - Keeps the event contract centralized and easy to reuse across /help, /try, /app.
 * - Makes auth sync behavior explicit and testable (no hidden side effects).
 *
 * ARCHITECTURE CONTEXT:
 * - Extension content script: packages/chrome-plugin/extension/content/web-auth-bridge.js
 * - Web pages dispatch events → content script → background → backend token exchange
 * - Content script dispatches gist-auth-state → web pages receive auth updates
 *
 * EVENT CONTRACT (MUST MATCH EXTENSION):
 * - gist-auth-request: no detail (web → extension)
 * - gist-auth-import: { idToken, email? } or { workerToken, email? } (web → extension)
 * - gist-auth-logout: no detail (web → extension)
 * - gist-settings-changed: no detail (web → extension; triggers settings refresh)
 * - gist-auth-state: { workerToken, email, profile } | null (extension → web)
 *
 * SECURITY NOTES:
 * - The extension enforces origin checks; the web helper simply emits/listens.
 * - This helper does NOT store tokens; callers decide how to use auth data.
 *
 * INTEGRATION REQUIREMENT:
 * - Any page that shows Sign In and should auto-skip login when the extension is already logged in
 *   must call `createWebAuthBridge({ onAuthState: ... }).listen()` and use the state to update UI
 *   and store/use `workerToken` for API calls.
 * - Calling `listen()` without an `onAuthState` callback still requests auth state from the extension,
 *   but it will not update UI or auth state on its own (there is no callback to receive it).
 *
 * SIMPLER IS BETTER:
 * - Small, explicit API.
 * - No hidden persistence or caching.
 * - No framework dependencies.
 *
 * @file src/client/web-auth-bridge.ts
 */

/**
 * DOM event names used by the web ↔ extension auth bridge.
 */
export const WebAuthBridgeEvents = Object.freeze({
  /** Event dispatched from web to extension to request current auth state. */
  AUTH_STATE_REQUEST: 'gist-auth-request',
  /** Event dispatched from extension to web with auth state. */
  AUTH_STATE: 'gist-auth-state',
  /** Event dispatched from web to extension to import Firebase auth. */
  AUTH_IMPORT: 'gist-auth-import',
  /** Event dispatched from web to extension to logout. */
  AUTH_LOGOUT: 'gist-auth-logout',
  /** Event dispatched from web to extension after server-backed prefs change. */
  SETTINGS_CHANGED: 'gist-settings-changed'
} as const);

/**
 * Normalized auth state delivered from the extension.
 */
export interface WebAuthState {
  workerToken: string;
  email: string | null;
  profile: Record<string, unknown> | null;
}

/**
 * Auth import payload sent from the web to the extension.
 */
export interface WebAuthImportPayload {
  idToken: string;
  email?: string | null;
}

/**
 * Passwordless worker session minted by the email magic-link completion route.
 */
export interface WebWorkerSessionImportPayload {
  workerToken: string;
  email?: string | null;
}

/**
 * Options for the web auth bridge.
 */
export interface WebAuthBridgeOptions {
  /**
   * Callback fired whenever gist-auth-state is dispatched.
   * Receives normalized auth state (or null when signed out).
   */
  onAuthState?: (state: WebAuthState | null) => void;
}

/**
 * Return type for the web auth bridge instance.
 */
export interface WebAuthBridge {
  /** Start listening for gist-auth-state events. */
  listen: () => void;
  /** Stop listening for gist-auth-state events. */
  dispose: () => void;
  /** Dispatch auth import event to extension. */
  sendAuthImport: (payload: WebAuthImportPayload) => void;
  /** Dispatch a verified worker-session import after passwordless email completion. */
  sendWorkerSessionImport: (payload: WebWorkerSessionImportPayload) => void;
  /** Dispatch auth logout event to extension. */
  sendLogout: () => void;
  /** Ask extension to refresh settings from backend (fire-and-forget). */
  notifySettingsChanged: () => void;
}

/**
 * Minimum contract for an EventTarget used by the bridge.
 */
interface BridgeEventTarget {
  addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
  removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
  dispatchEvent: (event: Event) => boolean;
}

/**
 * Safe constructor for CustomEvent in environments that don't provide it.
 *
 * @param type - Event name
 * @param detail - Payload detail
 * @param bubbles - Whether event should bubble
 */
function createBridgeEvent(type: string, detail: unknown, bubbles: boolean): Event & { detail?: unknown } {
  if (typeof CustomEvent === 'function') {
    return new CustomEvent(type, { detail, bubbles, cancelable: false });
  }

  const event = new Event(type);
  Object.defineProperty(event, 'detail', {
    value: detail,
    writable: false,
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(event, 'bubbles', {
    value: bubbles,
    writable: false,
    enumerable: false,
    configurable: true
  });
  return event as Event & { detail?: unknown };
}

/**
 * Normalize raw auth payload into a well-formed WebAuthState.
 *
 * @param payload - Raw detail from CustomEvent
 */
function normalizeAuthState(payload: unknown): WebAuthState | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const maybe = payload as { workerToken?: unknown; email?: unknown; profile?: unknown };
  if (typeof maybe.workerToken !== 'string' || maybe.workerToken.length === 0) {
    return null;
  }

  const profile = typeof maybe.profile === 'object' && maybe.profile
    ? maybe.profile as Record<string, unknown>
    : null;
  const email = typeof maybe.email === 'string' && maybe.email.length > 0
    ? maybe.email
    : typeof profile?.email === 'string' && profile.email.length > 0
      ? profile.email
      : null;

  return {
    workerToken: maybe.workerToken,
    email,
    profile
  };
}

/**
 * Build a minimal list of targets for event dispatch/listen.
 *
 * Document is preferred for dispatch (bubbling), but we also listen on window
 * to support pages that only listen there.
 */
function resolveBridgeTargets(): BridgeEventTarget[] {
  const targets: BridgeEventTarget[] = [];

  const doc = typeof globalThis !== 'undefined'
    ? (globalThis as { document?: Document }).document
    : undefined;

  if (doc && typeof doc.addEventListener === 'function') {
    targets.push(doc as unknown as BridgeEventTarget);
  }

  const win = typeof globalThis !== 'undefined'
    ? (globalThis as { window?: Window }).window
    : undefined;

  if (win && typeof win.addEventListener === 'function') {
    targets.push(win as unknown as BridgeEventTarget);
  }

  return targets;
}

/**
 * Create a web auth bridge instance for the current page.
 *
 * @param options - Bridge options and callbacks
 * @returns WebAuthBridge instance
 */
export function createWebAuthBridge(options: WebAuthBridgeOptions = {}): WebAuthBridge {
  const { onAuthState } = options;
  const targets = resolveBridgeTargets();
  const bubble = true;
  let listening = false;
  const handledEvents = new WeakSet<Event>();

  /**
   * Handles incoming gist-auth-state events from the extension content script.
   *
   * SECURITY (A-028): Validates event shape before processing:
   * 1. Rejects non-CustomEvent instances (blocks synthetic Event dispatches).
   * 2. normalizeAuthState() validates detail is an object with a string workerToken.
   * 3. Events with invalid shape are silently ignored (no callback invoked).
   *
   * CSP (A-014) already restricts which scripts can run on the page, but
   * defense-in-depth requires validating the payload shape regardless.
   */
  const authStateHandler = (event: Event) => {
    if (handledEvents.has(event)) return;
    handledEvents.add(event);
    if (!(event instanceof CustomEvent)) {
      return;
    }

    const { detail } = event;

    // normalizeAuthState rejects anything that isn't {workerToken: string, ...}
    const normalized = normalizeAuthState(detail);
    onAuthState?.(normalized);
  };

  const listen = () => {
    if (listening) {
      return;
    }
    listening = true;
    targets.forEach((target) => {
      target.addEventListener(WebAuthBridgeEvents.AUTH_STATE, authStateHandler);
    });

    // Request a fresh auth state now that our listener is attached.
    // This avoids a race where the content script dispatches gist-auth-state
    // before the page bundle finishes loading and registers listeners.
    dispatch(WebAuthBridgeEvents.AUTH_STATE_REQUEST, null);
  };

  const dispose = () => {
    if (!listening) {
      return;
    }
    listening = false;
    targets.forEach((target) => {
      target.removeEventListener(WebAuthBridgeEvents.AUTH_STATE, authStateHandler);
    });
  };

  const dispatch = (type: string, detail: unknown) => {
    if (targets.length === 0) {
      return;
    }

    const event = createBridgeEvent(type, detail, bubble);
    const preferred = targets[0];
    preferred.dispatchEvent(event);
  };

  const sendAuthImport = (payload: WebAuthImportPayload) => {
    dispatch(WebAuthBridgeEvents.AUTH_IMPORT, {
      idToken: payload.idToken,
      email: payload.email ?? null
    });
  };

  const sendWorkerSessionImport = (payload: WebWorkerSessionImportPayload) => {
    dispatch(WebAuthBridgeEvents.AUTH_IMPORT, {
      workerToken: payload.workerToken,
      email: payload.email ?? null
    });
  };

  const sendLogout = () => {
    dispatch(WebAuthBridgeEvents.AUTH_LOGOUT, null);
  };

  const notifySettingsChanged = () => {
    dispatch(WebAuthBridgeEvents.SETTINGS_CHANGED, null);
  };

  return {
    listen,
    dispose,
    sendAuthImport,
    sendWorkerSessionImport,
    sendLogout,
    notifySettingsChanged
  };
}
